import { ContentSubmissionUnit, DeliverableLink, isVoided } from '@foodfluence/shared';

/**
 * Gets all deliverable links related to a specific content unit.
 *
 * Voided slots are excluded: the brand has withdrawn them, so the creator neither sees nor owes them.
 */
export const getUnitRelatedLinks = (
  unitId: string,
  deliverableLinksRecord: Record<string, DeliverableLink[]>
): DeliverableLink[] => {
  return Object.values(deliverableLinksRecord).reduce((acc, arr) => {
    if (Array.isArray(arr)) {
      acc.push(...arr.filter(l => l?.contentId === unitId && !isVoided(l)));
    }
    return acc;
  }, [] as DeliverableLink[]);
};

export interface DeliverableLinkRef {
  deliverableKey: string;
  linkIndex: number;
  link: DeliverableLink;
}

/**
 * Like getUnitRelatedLinks, but keeps each link's deliverableKey and index so
 * the caller can target it (e.g. open InputLinkModal for a specific channel slot).
 *
 * `linkIndex` stays the true position in `deliverableLinks[deliverableKey]` — voided slots are
 * skipped, never spliced out, because that index is what submission writes address.
 */
export const getUnitRelatedLinkRefs = (
  unitId: string,
  deliverableLinksRecord: Record<string, DeliverableLink[]>
): DeliverableLinkRef[] => {
  const refs: DeliverableLinkRef[] = [];
  Object.entries(deliverableLinksRecord).forEach(([deliverableKey, arr]) => {
    if (Array.isArray(arr)) {
      arr.forEach((link, linkIndex) => {
        if (link?.contentId === unitId && !isVoided(link)) {
          refs.push({ deliverableKey, linkIndex, link });
        }
      });
    }
  });
  return refs;
};

/**
 * Deliverable keys for this unit that still need a live post link — i.e. the
 * platforms the influencer has left to post on. Rejected links count as
 * outstanding; approved and under-review ones do not.
 */
export const getOutstandingPostChannels = (
  unitId: string,
  deliverableLinksRecord: Record<string, DeliverableLink[]>
): string[] => {
  const outstanding = getUnitRelatedLinkRefs(unitId, deliverableLinksRecord)
    .filter(({ link }) => link.status === 'pendingSubmission' || Boolean(link.rejectionReason))
    .map(({ deliverableKey }) => deliverableKey);
  return Array.from(new Set(outstanding)).sort();
};

/**
 * Returns the effective display status for a content unit card,
 * falling back to the related deliverable link status when the unit
 * is approved but channel posts still need to be submitted.
 */
export const getEffectiveContentStatus = (
  unit: ContentSubmissionUnit,
  links: DeliverableLink[]
): string => {
  if (unit.contentRejected) return 'contentRejected';
  if (unit.status !== 'approved') return unit.status;

  if (links.length === 0) return 'approved';
  if (links.some(l => l.rejectionReason)) return 'linkNeedsEdit';
  if (links.every(l => l.status === 'approved')) return 'approved';
  if (links.every(l => l.status === 'pendingVerification' || l.status === 'approved')) return 'linkPendingVerification';
  return 'linkPendingSubmission';
};